import * as api from '@apis/discount-coupon';
import * as productApi from '@apis/product';
import * as productCollectionApi from '@apis/product-collection';
import { CreateOrUpdateDiscountCouponBody, DiscountCoupon } from '@models/discount-coupon';
import { ProductModel } from '@models/product';
import { ProductCollection } from '@models/productCollection';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Checkbox, DatePicker, Input, Modal, Select, InputNumber, Tag, Radio } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { useState, useEffect } from 'react';
import { SelectProductModal } from '../product/SelectProductModal';
import { SelectProductCollection } from '../feed/SelectProductCollection';

export interface DiscountCouponCreateModalProps {
  open?: boolean;
  onClose?: () => void;
  needRefetch?: () => void;
  coupon?: DiscountCoupon; // edit 모드일 때 전달되는 쿠폰 데이터
}

const DISCOUNT_TYPE_OPTIONS = [
  { label: '금액 할인', value: 'amount' },
  { label: '퍼센트 할인', value: 'percentage' },
  { label: '구매수량 당 할인', value: 'unit' },
];

const ISSUANCE_TYPE_OPTIONS = [
  { label: '다운로드', value: 'download' },
  { label: '직접 발급', value: 'direct' },
];

const ISSUED_BY_TYPE_OPTIONS = [
  { label: '파트너', value: 'partner' },
  { label: '관리자', value: 'admin' },
  { label: '시스템', value: 'system' },
];

const COST_BEARER_TYPE_OPTIONS = [
  { label: '파트너', value: 'partner' },
  { label: '관리자', value: 'admin' },
];

const PARTNER_CONTRIBUTION_METHOD_OPTIONS = [
  { label: '고정금액', value: 'amount' },
  { label: '비율', value: 'percentage' },
];

export const DiscountCouponCreateModal: React.FC<DiscountCouponCreateModalProps> = (props) => {
  const isEditMode = !!props.coupon;
  const [createDiscountCouponData, setCreateDiscountCouponData] = useState<CreateOrUpdateDiscountCouponBody>({
    name: '',
    description: '',
    issuanceType: 'download',
    discountType: 'amount',
    minPurchaseAmount: 0,
    isStackable: false,
    appOnly: false,
    addCollectionProduct: false,
    usageLimitPerUser: 1,
    issueLimitPerUser: 1,
    discountUnitLimit: 0,
    discountAmountPerUnit: 0,
    usageLimit: 0,
    quantity: 0,
    issueStartDate: undefined,
    issueEndDate: undefined,
    validMode: 'manual',
    validFrom: undefined,
    validUntil: undefined,
    applicability: {
      type: 'products',
    },
    issuedBy: {
      type: 'admin',
      id: '', // 실제로는 현재 로그인한 사용자 ID를 사용해야 함
    },
    costBearer: {
      type: 'admin',
      id: '', // 실제로는 현재 로그인한 사용자 ID를 사용해야 함
    },
    conditions: undefined,
  });

  const [isProductSelectModalOpen, setIsProductSelectModalOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<ProductModel[]>([]);
  const [selectedCollections, setSelectedCollections] = useState<ProductCollection[]>([]);

  const dialog = useOkCancelDialog();

  // 기존 선택된 상품들 로드
  const selectedProductIds = createDiscountCouponData?.applicability?.productIds || [];
  const selectedCollectionIds = createDiscountCouponData?.applicability?.collections?.map((c) => c.id) || [];

  const selectedProductsQuery = useQuery({
    queryKey: ['selected-products', selectedProductIds],
    queryFn: async () => {
      if (selectedProductIds.length === 0) return [];
      const promises = selectedProductIds.map((id) => productApi.getProduct(id));
      const results = await Promise.all(promises);
      return results.map((result) => result.data).filter(Boolean);
    },
    enabled: selectedProductIds.length > 0 && isEditMode,
  });

  const selectedCollectionsQuery = useQuery({
    queryKey: ['selected-collections', selectedCollectionIds],
    queryFn: async () => {
      if (selectedCollectionIds.length === 0) return [];
      const promises = selectedCollectionIds.map((id) => productCollectionApi.getProductCollection(id));
      const results = await Promise.all(promises);
      return results.map((result) => result.data).filter(Boolean);
    },
    enabled: selectedCollectionIds.length > 0 && isEditMode,
  });

  // 모달이 열릴 때 edit 데이터 로드
  useEffect(() => {
    if (props.open && isEditMode && props.coupon) {
      // Edit 모드: 기존 쿠폰 데이터로 폼 초기화
      setCreateDiscountCouponData({
        name: props.coupon.name,
        description: props.coupon.description,
        issuanceType: props.coupon.issuanceType.toLowerCase(),
        discountType: props.coupon.discountType.toLowerCase(),
        discountAmount: props.coupon.discountAmount,
        discountPercent: props.coupon.discountPercent,
        maxDiscountAmount: props.coupon.maxDiscountAmount,
        minPurchaseAmount: props.coupon.minPurchaseAmount,
        discountUnitLimit: props.coupon.discountUnitLimit,
        discountAmountPerUnit: props.coupon.discountAmountPerUnit,
        isStackable: props.coupon.isStackable,
        appOnly: props.coupon.appOnly,
        addCollectionProduct: false, // 기존 쿠폰에는 이 필드가 없으므로 기본값 false
        usageLimitPerUser: props.coupon.usageLimitPerUser || 0,
        issueLimitPerUser: props.coupon.issueLimitPerUser || 0,
        usageLimit: props.coupon.usageLimit,
        quantity: props.coupon.quantity,
        issueStartDate: props.coupon.issueStartDate,
        issueEndDate: props.coupon.issueEndDate,
        validMode: props.coupon.validMode,
        validFrom: props.coupon.validFrom,
        validUntil: props.coupon.validUntil,
        applicability: {
          type: props.coupon.applicability.type.toLowerCase(),
          productIds: props.coupon.applicability.productIds,
          collections: props.coupon.applicability.collections,
        },
        issuedBy: {
          ...props.coupon.issuedBy,
          type: props.coupon.issuedBy.type?.toLowerCase?.() ?? props.coupon.issuedBy.type,
        },
        costBearer: {
          ...props.coupon.costBearer,
          type: props.coupon.costBearer.type?.toLowerCase?.() ?? props.coupon.costBearer.type,
        },
        precautions:
          props.coupon.precautions && props.coupon.precautions.length > 0 ? props.coupon.precautions : undefined,
        conditions: props.coupon.conditions,
      });
    }
    setIsProductSelectModalOpen(false);
  }, [props.open, props.coupon, isEditMode]);

  // 모달이 닫힐 때 데이터 초기화
  useEffect(() => {
    if (!props.open) {
      // 모달이 닫힐 때 모든 데이터 초기화
      setCreateDiscountCouponData({
        name: '',
        description: '',
        issuanceType: 'download',
        discountType: 'amount',
        minPurchaseAmount: 0,
        isStackable: false,
        appOnly: false,
        addCollectionProduct: false,
        usageLimitPerUser: 1,
        issueLimitPerUser: 1,
        usageLimit: 0,
        quantity: 0,
        issueStartDate: undefined,
        issueEndDate: undefined,
        validMode: 'manual',
        validFrom: undefined,
        validUntil: undefined,
        applicability: {
          type: 'products',
        },
        issuedBy: {
          type: 'admin',
          id: '',
        },
        costBearer: {
          type: 'admin',
          id: '',
        },
        conditions: undefined,
      });
      setSelectedProducts([]);
      setSelectedCollections([]);
    }
  }, [props.open]);

  // 선택된 상품과 기획전 데이터를 상태에 반영
  useEffect(() => {
    if (selectedProductsQuery.data) {
      setSelectedProducts(selectedProductsQuery.data);
    }
  }, [selectedProductsQuery.data]);

  useEffect(() => {
    if (selectedCollectionsQuery.data) {
      setSelectedCollections(selectedCollectionsQuery.data);
    }
  }, [selectedCollectionsQuery.data]);

  const createDiscountCouponMutation = useMutation({
    mutationFn: api.createDiscountCoupon,
    onSuccess: async () => {
      props.onClose && props.onClose();
      props.needRefetch && props.needRefetch();
      dialog.open({
        content: '생성되었습니다.',
        type: 'ok',
      });
    },
    onError: async (e: any) => {
      props.onClose && props.onClose();
      props.needRefetch && props.needRefetch();
      dialog.open({
        title: '오류',
        content: e?.response?.data?.message,
        type: 'ok',
      });
    },
  });

  const updateDiscountCouponMutation = useMutation({
    mutationFn: api.updateDiscountCoupon,
    onSuccess: async () => {
      props.onClose && props.onClose();
      props.needRefetch && props.needRefetch();
      dialog.open({
        content: '수정되었습니다.',
        type: 'ok',
      });
    },
    onError: async (e: any) => {
      dialog.open({
        title: '오류',
        content: e?.response?.data?.message,
        type: 'ok',
      });
    },
  });

  const handleSave = () => {
    if (isEditMode && props.coupon) {
      // Edit 모드: update API 호출 (CreateOrUpdateDiscountCouponBody 타입 사용)
      updateDiscountCouponMutation.mutate({
        couponId: props.coupon._id,
        body: createDiscountCouponData,
      });
    } else {
      // Create 모드: create API 호출
      createDiscountCouponMutation.mutate(createDiscountCouponData);
    }
  };

  return (
    <Modal
      width={1200}
      styles={{ footer: { height: '44px' } }}
      open={props.open}
      footer={
        <div style={{ width: '100%' }}>
          <Button
            style={{
              width: 120,
            }}
            onClick={props.onClose}>
            취소
          </Button>
          <Button
            style={{
              width: 120,
              marginLeft: '8px',
            }}
            onClick={handleSave}
            loading={createDiscountCouponMutation.isPending || updateDiscountCouponMutation.isPending}>
            {isEditMode ? '수정' : '생성'}
          </Button>
        </div>
      }
      onCancel={props.onClose}>
      <OverlayScrollbarsComponent
        defer
        options={{ scrollbars: { autoHide: 'scroll' } }}
        style={{
          backgroundColor: '#ffffff',
          marginTop: 0,
          paddingBottom: '100px',
          position: 'relative',
          height: 'calc(100dvh - 340px)',
          overflow: 'auto',
        }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: 10,
            }}>
            {isEditMode ? '할인 쿠폰 수정' : '할인 쿠폰 생성'}
          </div>
          <div style={{ display: 'flex', gap: '20px', padding: '0 50px' }}>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ marginTop: 10 }}>
                쿠폰 이름
                <Input
                  placeholder="쿠폰 이름을 입력하세요"
                  value={createDiscountCouponData?.name}
                  onChange={(e) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }));
                  }}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                쿠폰 설명
                <Input
                  placeholder="쿠폰 설명을 입력하세요"
                  value={createDiscountCouponData?.description}
                  onChange={(e) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }));
                  }}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                발급 방식
                <Select
                  style={{ width: '100%', marginTop: 4 }}
                  value={createDiscountCouponData?.issuanceType}
                  onChange={(value) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      issuanceType: value,
                    }));
                  }}
                  options={ISSUANCE_TYPE_OPTIONS}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                할인 타입
                <Select
                  style={{ width: '100%', marginTop: 4 }}
                  value={createDiscountCouponData?.discountType}
                  onChange={(value) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      discountType: value,
                    }));
                  }}
                  options={DISCOUNT_TYPE_OPTIONS}
                />
              </div>
              {createDiscountCouponData?.discountType === 'percentage' && (
                <>
                  <div style={{ marginTop: 10 }}>
                    할인 퍼센트
                    <InputNumber
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="할인 퍼센트를 입력하세요 (예: 10)"
                      value={
                        createDiscountCouponData?.discountPercent
                          ? Number(createDiscountCouponData.discountPercent)
                          : undefined
                      }
                      onChange={(value) => {
                        setCreateDiscountCouponData((prev) => ({
                          ...prev,
                          discountPercent: value?.toString() || '',
                        }));
                      }}
                      min={0}
                      max={100}
                      addonAfter="%"
                    />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    최대 할인 금액
                    <InputNumber
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="최대 할인 금액을 입력하세요"
                      value={createDiscountCouponData?.maxDiscountAmount}
                      onChange={(value) => {
                        setCreateDiscountCouponData((prev) => ({
                          ...prev,
                          maxDiscountAmount: value || 0,
                        }));
                      }}
                      min={0}
                      addonAfter="원"
                    />
                  </div>
                </>
              )}
              {createDiscountCouponData?.discountType === 'amount' && (
                <div style={{ marginTop: 10 }}>
                  할인 금액
                  <InputNumber
                    style={{ width: '100%', marginTop: 4 }}
                    placeholder="할인 금액을 입력하세요"
                    value={createDiscountCouponData?.discountAmount}
                    onChange={(value) => {
                      setCreateDiscountCouponData((prev) => ({
                        ...prev,
                        discountAmount: value || 0,
                      }));
                    }}
                    min={0}
                    addonAfter="원"
                  />
                </div>
              )}
              {createDiscountCouponData?.discountType === 'unit' && (
                <>
                  <div style={{ marginTop: 10 }}>
                    최대 할인 적용 수량
                    <InputNumber
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="최대 수량을 입력하세요 (예: 10)"
                      value={
                        createDiscountCouponData?.discountUnitLimit
                          ? Number(createDiscountCouponData.discountUnitLimit)
                          : undefined
                      }
                      onChange={(value) => {
                        setCreateDiscountCouponData((prev) => ({
                          ...prev,
                          discountUnitLimit: value || 0,
                        }));
                      }}
                      min={0}
                      max={100}
                      addonAfter="개"
                    />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    수량 당 할인금액
                    <InputNumber
                      style={{ width: '100%', marginTop: 4 }}
                      placeholder="할인 금액을 입력하세요"
                      value={createDiscountCouponData?.discountAmountPerUnit}
                      onChange={(value) => {
                        setCreateDiscountCouponData((prev) => ({
                          ...prev,
                          discountAmountPerUnit: value || 0,
                        }));
                      }}
                      min={0}
                      addonAfter="원"
                    />
                  </div>
                </>
              )}
              <div style={{ marginTop: 10 }}>
                최소 주문 금액
                <InputNumber
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder="최소 주문 금액을 입력하세요"
                  value={createDiscountCouponData?.minPurchaseAmount}
                  onChange={(value) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      minPurchaseAmount: value || 0,
                    }));
                  }}
                  min={0}
                  addonAfter="원"
                />
              </div>
              {/* <div style={{ marginTop: 10 }}>
                중복 적용 가능
                <Checkbox
                  style={{ marginTop: '4px', marginLeft: '20px' }}
                  checked={createDiscountCouponData?.isStackable}
                  onChange={(e) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      isStackable: e.target.checked,
                    }));
                  }}>
                  다른 쿠폰과 중복 적용 가능
                </Checkbox>
              </div> */}
              <div style={{ marginTop: 10 }}>
                앱 전용 쿠폰
                <Checkbox
                  style={{ marginTop: '4px', marginLeft: '20px' }}
                  checked={createDiscountCouponData?.appOnly}
                  onChange={(e) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      appOnly: e.target.checked,
                    }));
                  }}>
                  모바일 앱에서만 사용 가능
                </Checkbox>
              </div>
              <div style={{ marginTop: 10 }}>
                유저당 최대 사용 횟수
                <InputNumber
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder="유저당 최대 사용 횟수를 입력하세요"
                  value={createDiscountCouponData?.usageLimitPerUser}
                  onChange={(value) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      usageLimitPerUser: value || 0,
                    }));
                  }}
                  min={0}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                유저당 최대 발급 횟수
                <InputNumber
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder="유저당 최대 발급 횟수를 입력하세요"
                  value={createDiscountCouponData?.issueLimitPerUser}
                  onChange={(value) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      issueLimitPerUser: value || 0,
                    }));
                  }}
                  min={0}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                전체 최대 사용 횟수
                <InputNumber
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder="전체 최대 사용 횟수를 입력하세요"
                  value={createDiscountCouponData?.usageLimit}
                  onChange={(value) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      usageLimit: value || 100,
                    }));
                  }}
                  min={0}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                발급 가능한 총 수량
                <InputNumber
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder="발급 가능한 총 수량을 입력하세요"
                  value={createDiscountCouponData?.quantity}
                  onChange={(value) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      quantity: value || 100,
                    }));
                  }}
                  min={0}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                발급 기간 시작
                <br />
                <DatePicker
                  style={{ width: '100%', marginTop: 4 }}
                  showTime={{ showSecond: false }}
                  value={
                    createDiscountCouponData?.issueStartDate ? dayjs(createDiscountCouponData.issueStartDate) : null
                  }
                  onChange={(value, dateString) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      issueStartDate: moment(dateString, 'YYYY-MM-DD HH:mm:00').toDate(),
                    }));
                  }}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                발급 기간 종료
                <br />
                <DatePicker
                  style={{ width: '100%', marginTop: 4 }}
                  showTime={{ showSecond: false }}
                  value={createDiscountCouponData?.issueEndDate ? dayjs(createDiscountCouponData.issueEndDate) : null}
                  onChange={(value, dateString) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      issueEndDate: moment(dateString, 'YYYY-MM-DD HH:mm:00').toDate(),
                    }));
                  }}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                사용 기한 설정 방식
                <br />
                <Radio.Group
                  value={createDiscountCouponData?.validMode}
                  onChange={(e) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      validMode: e.target.value,
                    }));
                  }}
                  style={{ marginTop: 4 }}>
                  <Radio value="manual">직접 설정</Radio>
                  <Radio value="midnight">발급 당일 자정까지</Radio>
                  <Radio value="24hours">발급 시점으로부터 24시간</Radio>
                </Radio.Group>
              </div>
              <div style={{ marginTop: 10 }}>
                사용 기한 시작
                <br />
                {createDiscountCouponData?.validMode === 'manual' ? (
                  <DatePicker
                    style={{ width: '100%', marginTop: 4 }}
                    showTime={{ showSecond: false }}
                    value={createDiscountCouponData?.validFrom ? dayjs(createDiscountCouponData.validFrom) : null}
                    onChange={(value, dateString) => {
                      setCreateDiscountCouponData((prev) => ({
                        ...prev,
                        validFrom: moment(dateString, 'YYYY-MM-DD HH:mm:00').toDate(),
                      }));
                    }}
                  />
                ) : (
                  <div style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: 4 }}>
                    <span style={{ color: '#666', fontSize: '14px' }}>발급 즉시 사용 가능합니다.</span>
                  </div>
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                사용 기한 종료
                <br />
                {createDiscountCouponData?.validMode === 'manual' ? (
                  <DatePicker
                    style={{ width: '100%', marginTop: 4 }}
                    showTime={{ showSecond: false }}
                    value={createDiscountCouponData?.validUntil ? dayjs(createDiscountCouponData.validUntil) : null}
                    onChange={(value, dateString) => {
                      setCreateDiscountCouponData((prev) => ({
                        ...prev,
                        validUntil: moment(dateString, 'YYYY-MM-DD HH:mm:00').toDate(),
                      }));
                    }}
                  />
                ) : createDiscountCouponData?.validMode === 'midnight' ? (
                  <div style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: 4 }}>
                    <span style={{ color: '#666', fontSize: '14px' }}>발급 당일 자정까지 자동 설정됩니다.</span>
                  </div>
                ) : (
                  <div style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: 4 }}>
                    <span style={{ color: '#666', fontSize: '14px' }}>
                      발급시점으로부터 24시간 후까지 자동 설정됩니다.
                    </span>
                  </div>
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                <span>기획전 선택</span>
                <Checkbox
                  style={{ marginLeft: 10, display: 'none' }}
                  checked={createDiscountCouponData.addCollectionProduct}
                  onChange={(e) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      addCollectionProduct: e.target.checked,
                    }));
                  }}>
                  기획전 상품 적용
                </Checkbox>
                <div style={{ marginTop: 8 }}>
                  <div style={{ marginBottom: 8 }}>
                    <SelectProductCollection
                      onSelect={(collection) => {
                        // 이미 선택된 기획전인지 확인
                        const isAlreadySelected = selectedCollections.some((c) => c._id === collection._id);
                        if (!isAlreadySelected) {
                          const newSelectedCollections = [...selectedCollections, collection];
                          setSelectedCollections(newSelectedCollections);
                          setCreateDiscountCouponData((prev) => ({
                            ...prev,
                            applicability: {
                              ...prev.applicability,
                              collections: newSelectedCollections.map((c) => ({
                                id: c._id,
                                coordinates: { y: 0, ratio: 0 }, // 기본값 설정
                                images: { active: '', inactive: '' }, // 기본 이미지 설정
                              })),
                            },
                          }));
                        }
                      }}
                    />
                  </div>
                  {selectedCollections.length > 0 && (
                    <div>
                      <div style={{ marginBottom: 4, fontWeight: 'bold' }}>
                        선택된 기획전 ({selectedCollections.length}개):
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 4,
                          height: '250px',
                          overflow: 'auto',
                          flexDirection: 'column',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '4px',
                        }}>
                        {selectedCollections.map((collection) => (
                          <Tag
                            key={collection._id}
                            closable
                            onClose={() => {
                              const newSelectedCollections = selectedCollections.filter(
                                (c) => c._id !== collection._id,
                              );
                              setSelectedCollections(newSelectedCollections);
                              setCreateDiscountCouponData((prev) => ({
                                ...prev,
                                applicability: {
                                  ...prev.applicability,
                                  collections: newSelectedCollections.map((c) => ({
                                    id: c._id,
                                    coordinates: { y: 0, ratio: 0 }, // 기본값 설정
                                    images: { active: '', inactive: '' }, // 기본 이미지 설정
                                  })),
                                },
                              }));
                            }}>
                            {collection.title}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>적용 상품 선택</span>
                  <Checkbox
                    checked={createDiscountCouponData?.applicability?.type === 'all'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCreateDiscountCouponData((prev) => ({
                          ...prev,
                          applicability: {
                            ...prev.applicability,
                            type: 'all',
                            productIds: undefined,
                          },
                        }));
                        setSelectedProducts([]);
                      } else {
                        setCreateDiscountCouponData((prev) => ({
                          ...prev,
                          applicability: {
                            ...prev.applicability,
                            type: 'products',
                            productIds: prev.applicability?.productIds || [],
                          },
                        }));
                      }
                    }}>
                    전체 상품 적용
                  </Checkbox>
                </div>
                {createDiscountCouponData?.applicability?.type !== 'all' && (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="primary"
                      onClick={() => setIsProductSelectModalOpen(true)}
                      style={{ marginBottom: 8 }}>
                      상품 선택
                    </Button>
                    {selectedProducts.length > 0 && (
                      <div>
                        <div style={{ marginBottom: 4, fontWeight: 'bold' }}>
                          선택된 상품 ({selectedProducts.length}개):
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 4,
                            height: '250px',
                            overflow: 'auto',
                            flexDirection: 'column',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '4px',
                          }}>
                          {selectedProducts.map((product) => (
                            <Tag
                              key={product._id}
                              closable
                              onClose={() => {
                                const newSelectedProducts = selectedProducts.filter((p) => p._id !== product._id);
                                setSelectedProducts(newSelectedProducts);
                                setCreateDiscountCouponData((prev) => ({
                                  ...prev,
                                  applicability: {
                                    ...prev.applicability,
                                    productIds: newSelectedProducts.map((p) => p._id),
                                  },
                                }));
                              }}>
                              {product.title}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                발급자
                <Select
                  style={{ width: '100%', marginTop: 4 }}
                  value={createDiscountCouponData?.issuedBy?.type}
                  onChange={(value) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      issuedBy: {
                        ...prev.issuedBy,
                        type: value,
                      },
                    }));
                  }}
                  options={ISSUED_BY_TYPE_OPTIONS}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                비용 부담자
                <Select
                  style={{ width: '100%', marginTop: 4 }}
                  value={createDiscountCouponData?.costBearer?.type}
                  onChange={(value) => {
                    setCreateDiscountCouponData((prev) => ({
                      ...prev,
                      costBearer: {
                        ...prev.costBearer,
                        type: value,
                        ...(value === 'admin' ? { isSplitCost: false, partnerContribution: undefined } : {}),
                      },
                    }));
                  }}
                  options={COST_BEARER_TYPE_OPTIONS}
                />
                {createDiscountCouponData?.costBearer?.type === 'partner' && (
                  <div style={{ marginTop: 12, padding: 12, border: '1px solid #d9d9d9', borderRadius: 6 }}>
                    <Checkbox
                      checked={!!createDiscountCouponData?.costBearer?.isSplitCost}
                      onChange={(e) => {
                        setCreateDiscountCouponData((prev) => ({
                          ...prev,
                          costBearer: {
                            ...prev.costBearer,
                            isSplitCost: e.target.checked,
                          },
                        }));
                      }}>
                      파트너 부분 분담
                    </Checkbox>
                    <div
                      style={{
                        marginTop: 8,
                        opacity: createDiscountCouponData?.costBearer?.isSplitCost ? 1 : 0.5,
                        pointerEvents: createDiscountCouponData?.costBearer?.isSplitCost ? 'auto' : 'none',
                      }}>
                      <div style={{ marginBottom: 8 }}>
                        방식
                        <Select
                          style={{ width: '100%', marginTop: 4 }}
                          value={createDiscountCouponData?.costBearer?.partnerContribution?.method ?? 'amount'}
                          onChange={(method: 'amount' | 'percentage') => {
                            setCreateDiscountCouponData((prev) => ({
                              ...prev,
                              costBearer: {
                                ...prev.costBearer,
                                partnerContribution: {
                                  method,
                                  value: 0,
                                },
                              },
                            }));
                          }}
                          options={PARTNER_CONTRIBUTION_METHOD_OPTIONS}
                        />
                      </div>
                      <div>
                        {createDiscountCouponData?.costBearer?.partnerContribution?.method === 'percentage'
                          ? '비율 (0~1, 예: 0.3 = 30%)'
                          : '금액'}
                        <InputNumber
                          style={{ width: '100%', marginTop: 4 }}
                          min={
                            createDiscountCouponData?.costBearer?.partnerContribution?.method === 'percentage' ? 0 : 0
                          }
                          max={
                            createDiscountCouponData?.costBearer?.partnerContribution?.method === 'percentage'
                              ? 1
                              : createDiscountCouponData?.discountType === 'amount'
                                ? createDiscountCouponData?.discountAmount ?? 0
                                : createDiscountCouponData?.maxDiscountAmount ?? 0
                          }
                          step={
                            createDiscountCouponData?.costBearer?.partnerContribution?.method === 'percentage' ? 0.1 : 1
                          }
                          value={createDiscountCouponData?.costBearer?.partnerContribution?.value ?? 0}
                          onChange={(value) => {
                            setCreateDiscountCouponData((prev) => ({
                              ...prev,
                              costBearer: {
                                ...prev.costBearer,
                                partnerContribution: {
                                  ...(prev.costBearer?.partnerContribution ?? { method: 'amount', value: 0 }),
                                  value: value ?? 0,
                                },
                              },
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ marginTop: 10 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>추가 적용조건</div>
                <div>
                  쿠폰 적용할 체험 일정
                  <div style={{ fontSize: 12 }}>
                    * 설정한 일정 옵션에만 쿠폰이 적용됩니다. (옵션 선택 시 설정한 기간이 보여집니다.)
                  </div>
                  <DatePicker.RangePicker
                    style={{ width: '100%', marginTop: 4 }}
                    showTime={{ format: 'HH:mm' }}
                    format="YYYY-MM-DD HH:mm"
                    value={[
                      createDiscountCouponData.conditions?.productOptionDateRange?.from
                        ? dayjs(createDiscountCouponData.conditions.productOptionDateRange.from)
                        : null,
                      createDiscountCouponData.conditions?.productOptionDateRange?.until
                        ? dayjs(createDiscountCouponData.conditions.productOptionDateRange.until)
                        : null,
                    ]}
                    onChange={(dates) => {
                      setCreateDiscountCouponData((prev) => ({
                        ...prev,
                        conditions: {
                          ...prev.conditions,
                          productOptionDateRange: dates
                            ? { from: dates[0]?.toDate(), until: dates[1]?.toDate() }
                            : null,
                        },
                      }));
                    }}
                    placeholder={['시작 날짜/시간', '종료 날짜/시간']}
                  />
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                유의사항
                <div style={{ marginTop: 8 }}>
                  {createDiscountCouponData.precautions?.map((precaution, index) => (
                    <div
                      key={index}
                      style={{ marginBottom: 16, padding: 12, border: '1px solid #d9d9d9', borderRadius: 6 }}>
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 'bold' }}>제목</span>
                        <Input
                          placeholder="유의사항 제목을 입력하세요"
                          value={precaution.title}
                          onChange={(e) => {
                            const newPrecautions = [...(createDiscountCouponData.precautions || [])];
                            newPrecautions[index] = { ...newPrecautions[index], title: e.target.value };
                            setCreateDiscountCouponData((prev) => ({
                              ...prev,
                              precautions: newPrecautions,
                            }));
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 'bold' }}>내용</span>
                        <Input.TextArea
                          placeholder="유의사항 내용을 입력하세요"
                          value={precaution.description}
                          onChange={(e) => {
                            const newPrecautions = [...(createDiscountCouponData.precautions || [])];
                            newPrecautions[index] = { ...newPrecautions[index], description: e.target.value };
                            setCreateDiscountCouponData((prev) => ({
                              ...prev,
                              precautions: newPrecautions,
                            }));
                          }}
                          rows={4}
                        />
                      </div>
                      <Button
                        type="link"
                        danger
                        onClick={() => {
                          const newPrecautions =
                            createDiscountCouponData.precautions?.filter((_, i) => i !== index) || [];
                          setCreateDiscountCouponData((prev) => ({
                            ...prev,
                            precautions: newPrecautions,
                          }));
                        }}
                        style={{ padding: 0 }}>
                        삭제
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => {
                      setCreateDiscountCouponData((prev) => ({
                        ...prev,
                        precautions: [...(prev.precautions || []), { title: '', description: '' }],
                      }));
                    }}
                    style={{ width: '100%' }}>
                    + 유의사항 추가
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </OverlayScrollbarsComponent>

      <SelectProductModal
        open={isProductSelectModalOpen}
        onClose={() => setIsProductSelectModalOpen(false)}
        onAddProducts={(products) => {
          setSelectedProducts(products);
          setCreateDiscountCouponData((prev) => ({
            ...prev,
            applicability: {
              ...prev.applicability,
              productIds: products.map((p) => p._id),
            },
          }));
          setIsProductSelectModalOpen(false);
        }}
      />
    </Modal>
  );
};
